import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Phone } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | error
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed","expired","refunded"].includes(data.payment_status)) { setStatus("failed"); return; }
        if (attempts.current >= 20) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 20) setStatus("error");
        else setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const amount = record?.amount ? (record.amount / 100).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid="payment-success-page">
      <Seo title="Paiement — DDSBillancourt" description="Confirmation de votre paiement en ligne pour votre formation à DDSBillancourt." path="/payment/success" noindex />
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 md:px-8 pt-32 pb-24">
        {status === "polling" && (
          <div className="text-center" data-testid="status-polling">
            <Loader2 className="w-16 h-16 text-[#00a86b] animate-spin mx-auto" />
            <h1 className="mt-6 font-headings font-black text-3xl md:text-4xl">Vérification de votre paiement…</h1>
            <p className="mt-3 text-[#5b6577]">Merci de patienter quelques secondes, nous confirmons la transaction auprès de Stripe.</p>
          </div>
        )}

        {status === "paid" && (
          <div className="text-center" data-testid="status-paid">
            <div className="w-20 h-20 rounded-full bg-[#00a86b]/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-12 h-12 text-[#00a86b]" />
            </div>
            <h1 className="mt-6 font-headings font-black text-3xl md:text-4xl">
              {record?.lookup_key === "evaluation_1h" ? "Merci ! Réservation confirmée." : "Paiement réussi"}
            </h1>
            <p className="mt-3 text-[#5b6577] text-lg">
              {record?.lookup_key === "evaluation_1h"
                ? "Merci pour votre paiement — nous vous recontactons rapidement pour fixer le rendez-vous de votre heure d'évaluation."
                : "Merci pour votre confiance ! Votre inscription est confirmée."}
            </p>
            {amount && (
              <div className="mt-6 inline-flex flex-col items-center bg-white border border-black/5 rounded-2xl px-8 py-5 shadow-sm">
                <div className="text-xs uppercase tracking-widest text-[#5b6577] font-headings font-bold">Montant réglé</div>
                <div className="mt-1 font-headings font-black text-4xl text-[#00a86b]">{amount} €</div>
                {record?.lookup_key && <div className="mt-1 text-xs text-[#5b6577]">{record.lookup_key}</div>}
              </div>
            )}
            <div className="mt-8 bg-white border border-black/5 rounded-2xl p-6 text-left shadow-sm">
              <div className="font-headings font-bold text-lg">Et maintenant ?</div>
              <ul className="mt-3 space-y-2 text-sm text-[#3a4557]">
                <li>• Un reçu vous a été envoyé par email par Stripe.</li>
                <li>• {record?.lookup_key === "evaluation_1h"
                       ? "Notre secrétariat vous contacte sous 24 h ouvrées pour fixer la date et l'heure de votre évaluation."
                       : "Notre équipe vous contacte sous 24 h ouvrées pour caler votre première leçon."}</li>
                <li>• Besoin de nous joindre plus tôt ? <a href="tel:+33141315061" className="text-[#00a86b] font-semibold">01 41 31 50 61</a></li>
              </ul>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/" className="inline-flex items-center gap-2 bg-[#1a2332] hover:bg-[#00a86b] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
                Retour à l&apos;accueil <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center" data-testid="status-failed">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="mt-6 font-headings font-black text-3xl">Paiement non finalisé</h1>
            <p className="mt-3 text-[#5b6577]">Le paiement n'a pas pu être finalisé. Aucun montant n'a été prélevé sur votre carte.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/#tarifs" className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#00915d] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
                Réessayer <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+33141315061" className="inline-flex items-center gap-2 border-2 border-[#1a2332]/15 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
                <Phone className="w-4 h-4" /> Nous appeler
              </a>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="status-error">
            <div className="w-20 h-20 rounded-full bg-yellow-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-12 h-12 text-yellow-500" />
            </div>
            <h1 className="mt-6 font-headings font-black text-3xl">Confirmation en cours</h1>
            <p className="mt-3 text-[#5b6577]">
              La confirmation prend plus de temps que prévu. Vous recevrez un email de Stripe dès que le paiement sera validé.
              En cas de doute, n'hésitez pas à nous appeler au <a href="tel:+33141315061" className="text-[#00a86b] font-semibold">01 41 31 50 61</a>.
            </p>
            <div className="mt-8">
              <Link to="/" className="inline-flex items-center gap-2 bg-[#1a2332] hover:bg-[#00a86b] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
                Retour à l'accueil
              </Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
